import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  FloatingPortal,
} from '@floating-ui/react';
import { useOptionalI18n } from '@/i18n';
import { localizeStringList, localizeTemplate, localizeText } from '@/i18n/options';

interface MultiSelectProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  displayMode?: 'tags' | 'count' | 'list';
  maxDisplayTags?: number;
  searchable?: boolean;
  showSelected?: boolean;
  allowCustom?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  className = '',
  displayMode = 'tags',
  maxDisplayTags = 2,
  searchable = true,
  showSelected = true,
  allowCustom = false,
}) => {
  const { locale } = useOptionalI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const referenceRef = useRef<HTMLElement | null>(null);

  const localizedOptions = useMemo(() => localizeStringList(locale, options), [locale, options]);
  const localizedValueMap = useMemo(
    () => new Map(options.map((option, index) => [option, localizedOptions[index] ?? option])),
    [localizedOptions, options]
  );

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            '--dropdown-max-height': `${Math.min(availableHeight - 8, 224)}px`,
          } as React.CSSProperties);
        },
      }),
    ],
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const referenceNode = referenceRef.current;
      const floatingNode = refs.floating.current;

      if (referenceNode?.contains(target) || floatingNode?.contains(target)) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [refs.floating]);

  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else if (searchQuery) {
      setSearchQuery('');
    }
  }, [isOpen, searchQuery, searchable]);

  const toggleOption = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const addCustomOption = (customValue: string) => {
    if (customValue.trim() && !value.includes(customValue.trim())) {
      onChange([...value, customValue.trim()]);
      setSearchQuery('');
    }
  };

  const removeOption = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== option));
  };

  const setReferenceNode = (node: HTMLElement | null) => {
    referenceRef.current = node;
    refs.setReference(node);
  };

  const renderLabel = (item: string) => localizedValueMap.get(item) ?? item;

  const renderSelectedDisplay = () => {
    if (value.length === 0) {
      return <span className="text-gray-400">{localizeText(locale, placeholder)}</span>;
    }

    switch (displayMode) {
      case 'count':
        return (
          <span className="font-medium text-gray-700">
            {value.length} {value.length === 1 ? localizeText(locale, 'item') : localizeText(locale, 'items')} {localizeText(locale, 'selected')}
          </span>
        );

      case 'list':
        return <span className="text-gray-700">{value.map(renderLabel).join(', ')}</span>;

      case 'tags':
      default: {
        const displayTags = value.slice(0, maxDisplayTags);
        const remaining = value.length - maxDisplayTags;

        return (
          <>
            {displayTags.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1 rounded-md border border-secondary/20 bg-secondary/10 px-2 py-0.5 text-sm text-secondary"
              >
                {renderLabel(item)}
                {!disabled && (
                  <button type="button" onClick={(e) => removeOption(item, e)} className="hover:text-secondary">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
            {remaining > 0 && (
              <span className="inline-flex items-center rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-sm text-gray-600">
                +{remaining} {localizeText(locale, 'more')}
              </span>
            )}
          </>
        );
      }
    }
  };

  const query = searchable ? searchQuery.trim().toLowerCase() : '';
  const filtered = query
    ? options.filter((option) => {
        const localized = renderLabel(option).toLowerCase();
        return option.toLowerCase().includes(query) || localized.includes(query);
      })
    : options;

  const showCustomOption =
    allowCustom && query && !options.includes(searchQuery.trim()) && !value.includes(searchQuery.trim());

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={setReferenceNode}
        className={`input-field flex min-h-[42px] cursor-pointer items-center justify-between transition-all ${
          disabled ? 'cursor-not-allowed bg-gray-100' : ''
        } ${isOpen ? 'border-2 border-primary ring-2 ring-primary ring-opacity-20' : ''}`}
      >
        <div className="flex flex-1 flex-wrap gap-1">
          {searchable && isOpen ? (
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={localizeText(locale, 'Search...')}
              className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
              onClick={(e) => e.stopPropagation()}
              onFocus={() => !disabled && setIsOpen(true)}
            />
          ) : showSelected ? (
            renderSelectedDisplay()
          ) : (
            <span className="text-gray-400">{localizeText(locale, placeholder)}</span>
          )}
        </div>
        <ChevronDown
          className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180 transform' : ''}`}
        />
      </div>

      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-[1040] overflow-hidden rounded-lg border-2 bg-white shadow-lg"
          >
            <div className="overflow-auto" style={{ maxHeight: 'var(--dropdown-max-height, 224px)' }}>
              {options.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">{localizeText(locale, 'No options available')}</div>
              ) : filtered.length === 0 && !showCustomOption ? (
                <div className="px-3 py-2 text-sm text-gray-500">{localizeText(locale, 'No results')}</div>
              ) : (
                <>
                  {filtered.map((option) => (
                    <div
                      key={option}
                      onClick={() => toggleOption(option)}
                      className={`flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-gray-50 ${
                        value.includes(option) ? 'bg-secondary/10' : ''
                      }`}
                    >
                      <span className={value.includes(option) ? 'font-medium text-secondary' : ''}>
                        {renderLabel(option)}
                      </span>
                      {value.includes(option) && (
                        <div className="flex h-5 w-5 items-center justify-center rounded bg-secondary text-white">
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                  ))}
                  {showCustomOption && (
                    <div
                      onClick={() => addCustomOption(searchQuery)}
                      className="flex cursor-pointer items-center border-t border-gray-200 px-3 py-2 font-medium text-accent hover:bg-accent/10"
                    >
                      + {localizeTemplate(locale, 'Use "{value}" as custom input', searchQuery.trim())}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default MultiSelect;
